import { AlertTriangle, Clock, Home, RefreshCw } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import { Button } from '@/components/ui/Button'
import { isApiError } from '@/lib/errors'
import { forwardChannel } from '@/lib/routing'
import { describeRouteError } from './describe'
import styles from './ErrorPanel.module.scss'

export interface ErrorPanelProps {
  error: unknown
  /** Called when the user hits "Tentar novamente" — typically the router's
   *  ``reset`` fn. When absent, a plain ``location.reload`` is offered. */
  onReset?: () => void
  /** Overrides the heading derived from ``error`` (e.g. the 404 page). */
  title?: string
  /** Overrides the text derived from ``error``. */
  description?: string
}

/**
 * Full-page fallback for unhandled errors at the route level. Shows a
 * friendly message tailored to the kind of failure when the error comes
 * from the API (``describeRouteError``), plus the request id (when present)
 * so the user can quote it in a bug report. Render errors get a generic
 * text; their raw message stays under "Detalhes técnicos".
 */
export function ErrorPanel({
  error,
  onReset,
  title: titleOverride,
  description: descriptionOverride,
}: ErrorPanelProps) {
  const router = useRouter()
  const described = describeRouteError(error)
  const title = titleOverride ?? described.title
  const description = descriptionOverride ?? described.description
  const { clock } = described
  const technical = titleOverride !== undefined ? null : described.technical
  const requestId = isApiError(error) ? error.problem?.request_id : null

  return (
    <div className={styles.shell} role="alert">
      <span className={styles.iconWrap} aria-hidden>
        {clock ? <Clock /> : <AlertTriangle />}
      </span>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.description}>{description}</p>
      {requestId ? <span className={styles.requestId}>req: {requestId}</span> : null}
      {technical ? (
        <details className={styles.technical}>
          <summary>Detalhes técnicos</summary>
          <code>{technical}</code>
        </details>
      ) : null}
      <div className={styles.actions}>
        <Button
          variant="secondary"
          leadingIcon={<Home />}
          onClick={() => {
            void router.navigate({ to: '/', search: forwardChannel })
          }}
        >
          Início
        </Button>
        <Button
          leadingIcon={<RefreshCw />}
          onClick={() => {
            if (onReset) onReset()
            else window.location.reload()
          }}
        >
          Tentar novamente
        </Button>
      </div>
    </div>
  )
}
